import { useState, type DragEvent } from 'react';
import {
  Button,
  Input,
  Tooltip,
  Spinner,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ChevronRight16Regular,
  ChevronDown16Regular,
  FolderAdd20Regular,
  MoreHorizontalRegular,
  MailArrowUp20Regular,
  TabAddRegular,
} from '@fluentui/react-icons';
import { FolderItemIcon, FolderOpenItemIcon } from '@/components/ItemIcon';
import { useQueryClient } from '@tanstack/react-query';
import type { Folder } from '@/types/domain';
import type { FolderNode } from './buildTree';
import { DraggableFolder } from './DraggableFolder';
import { MoveFolderDialog } from './MoveFolderDialog';
import { useTreeStore } from '@/stores/treeStore';
import { useSelectionStore } from '@/stores/selectionStore';
import { useMailDropStore } from '@/stores/mailDropStore';
import { useIsFiling } from '@/stores/filingStore';
import { useFileMail } from '@/features/file-mail/useFileMail';
import { useFileMailToasts } from '@/features/multi-select/useFileMailToasts';
import { useDropFiler } from '@/features/file-mail/useDropFiler';
import { useItemClipboard } from '@/hooks/useItemClipboard';
import { ItemActionMenuItems } from '@/components/ItemActionMenuItems';
import { FileHereTooltip } from '@/components/FileHereTooltip';
import { menuPositioning, pointTarget, type CursorTarget } from '@/components/cursorMenu';
import { useRowSelection } from '@/features/multi-select/useRowSelection';
import { useMultiSelectionMenu } from '@/features/multi-select/useMultiSelectionMenu';
import { treeScope, type SelectedRef } from '@/features/multi-select/selection';
import { useRenameFolder, useDeleteFolder } from '@/hooks/useFolderMutations';
import { useCreateFolder } from '@/hooks/useCreateFolder';
import { extractDroppedFiles } from '@/services/office/dragDrop';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { FolderMails } from './FolderMails';

const useStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    padding: `0 ${tokens.spacingHorizontalXS}`,
    height: '28px',
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusSmall,
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover },
  },
  rowActive: { backgroundColor: tokens.colorBrandBackground2 },
  // Mail-drop target highlight — matches the folder-reorder DnD affordance
  // (DraggableFolder.over) so dropping a mail lights up the hovered folder,
  // not the whole pane.
  rowDropOver: {
    outline: `2px dashed ${tokens.colorBrandStroke1}`,
    outlineOffset: '-2px',
    backgroundColor: tokens.colorBrandBackground2,
  },
  label: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  actions: { display: 'flex', alignItems: 'center', width: '24px', justifyContent: 'center' },
  // A non-expandable folder shows no chevron. Reserve the chevron button's exact
  // footprint (a hidden clone) so the folder icon / name stay aligned with
  // expandable siblings.
  chevronSpacer: { visibility: 'hidden', pointerEvents: 'none' },
  // A little breathing room between the folder icon and its label.
  rowIcon: { marginRight: tokens.spacingHorizontalXS, flexShrink: 0 },
  hidden: { display: 'none' },
  visible: {},
  // Conteneur du menu ⋯ : occupe exactement la place du bouton qu'il encapsule.
  menuWrap: { display: 'flex', flexShrink: 0 },
  children: { paddingLeft: tokens.spacingHorizontalM },
  renameInput: { flex: 1 },
  subCreateRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
  },
  // Les styles de l'infobulle riche « Ranger ici » vivaient ici, recopiés à
  // l'identique dans FolderDetail. Ils sont désormais dans le composant partagé
  // `components/FileHereTooltip`, qui porte aussi le contenu.
});

interface Props {
  node: FolderNode;
  depth: number;
}

export function FolderRow({ node, depth }: Props) {
  const styles = useStyles();
  const expanded = useTreeStore((s) => s.expanded.has(node.folder.id));
  const toggleExpanded = useTreeStore((s) => s.toggleExpanded);
  const activeFolderId = useTreeStore((s) => s.activeFolderId);
  const setActiveFolder = useTreeStore((s) => s.setActiveFolder);
  const openTab = useTreeStore((s) => s.openTab);
  const queryClient = useQueryClient();
  const selection = useSelectionStore((s) => s.items);
  const fileMail = useFileMail();
  const { showLoading, showResults, showError } = useFileMailToasts();
  const { fileFiles } = useDropFiler();
  const { canPaste, pending, isBusy, copyFolder, cutFolder, pasteInto } = useItemClipboard();
  const expand = useTreeStore((s) => s.expand);
  const openFolderDetail = useTreeStore((s) => s.openFolderDetail);
  const setDrag = useMailDropStore((s) => s.setDrag);
  const endDrag = useMailDropStore((s) => s.endDrag);
  const dropOver = useMailDropStore(
    (s) => s.target?.kind === 'folder' && s.target.folderId === node.folder.id,
  );
  const isFiling = useIsFiling(node.folder.id);
  const renameMut = useRenameFolder();
  const deleteMut = useDeleteFolder();
  const createMut = useCreateFolder();
  const [hover, setHover] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Ancre du menu : un point quand il vient d'un clic droit, `null` quand il
  // vient du bouton ⋯ (il reprend alors son ancrage sur le bouton).
  const [menuTarget, setMenuTarget] = useState<CursorTarget | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(node.folder.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Un seul état pour le dialogue de destination : déplacement ou copie.
  const [targetMode, setTargetMode] = useState<'move' | 'copy' | null>(null);
  const [creatingChild, setCreatingChild] = useState(false);
  const [childName, setChildName] = useState('');

  // Ce que « Copier » / « Couper » mettent dans le presse-papiers. `parentId`
  // sert au serveur comme au client : coller un dossier coupé dans son propre
  // parent est un déplacement sur place, donc ignoré.
  const clipboardFolder = {
    folderId: node.folder.id,
    containerId: node.folder.containerId,
    parentId: node.folder.parentId,
    name: node.folder.name,
  };

  const sel = useRowSelection(treeScope(node.folder.containerId));
  const selected = sel.isSelected(node.folder.id);
  const multi = useMultiSelectionMenu({ items: sel.items, onDone: () => setMenuOpen(false) });
  const ref: SelectedRef = {
    kind: 'folder',
    id: node.folder.id,
    containerId: node.folder.containerId,
    parentId: node.folder.parentId,
    label: node.folder.name,
  };

  const isActive = activeFolderId === node.folder.id;
  // A folder is expandable if it has subfolders OR mails filed directly in it.
  // Truly-empty leaf folders (no subfolders, no mails) get no chevron and show
  // nothing beneath. An unknown mailCount (older edge function not yet shipping
  // it) is treated as "might have mails" so we never hide the chevron on a
  // folder that actually has mail — degrades gracefully regardless of deploy
  // order.
  const hasChildren = node.children.length > 0;
  const hasMails = node.folder.mailCount === undefined || node.folder.mailCount > 0;
  const isExpandable = hasChildren || hasMails;
  const showFileButton = hover && selection.length > 0;

  const onClick = (e: React.MouseEvent) => {
    if (renaming) return;
    // CTRL/MAJ+clic construit la multi-sélection ; il ne doit pas AUSSI changer le
    // dossier actif, qui pilote l'affichage de droite.
    if (sel.onRowClick(e, ref)) return;
    setActiveFolder(node.folder.id);
    addBreadcrumb({ category: 'ui.click', message: 'select-folder', data: { id: node.folder.id } });
  };

  const onFileHere = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    addBreadcrumb({
      category: 'ui.click',
      message: 'file-here',
      data: { folderId: node.folder.id, itemCount: selection.length },
    });
    const toastId = showLoading(selection.length);
    fileMail.mutate(
      { targetFolderId: node.folder.id, targetContainerId: node.folder.containerId, items: selection },
      {
        // Feedback was previously never surfaced (showResults was dead code), so
        // filing looked like it did nothing whether it succeeded or failed.
        onSuccess: (results) => {
          showResults(results, toastId);
          // Reveal the folder so the freshly filed mail is immediately visible.
          if (results.some((r) => r.status === 'created')) expand(node.folder.id);
        },
        onError: (err) => showError(err instanceof Error ? err.message : String(err), toastId),
      },
    );
  };

  // Native HTML5 drag target (Classic Outlook desktop / Mac). New Outlook / web
  // don't raise these on the pane — there the Office DragAndDropEvent drives the
  // highlight (via mailDropStore) and drop instead. `types` includes 'Files'
  // when an actual message is being dragged.
  const isFileDrag = (e: DragEvent) => e.dataTransfer?.types?.includes('Files');
  const onRowDragOver = (e: DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setDrag({ kind: 'folder', folderId: node.folder.id });
  };
  const onRowDragLeave = (e: DragEvent) => {
    // Ignore leaves into a child element of the same row. Leaving to blank space
    // clears the folder hover; a subsequent dragover (a sibling folder, the
    // project section → root, or the pane root) sets the next target, and
    // leaving the pane entirely is cleared by DropZone's root dragleave.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDrag(null);
  };
  const onRowDrop = (e: DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    endDrag();
    const files = extractDroppedFiles(e.nativeEvent);
    addBreadcrumb({
      category: 'office.event',
      message: 'drop-on-folder',
      data: { folderId: node.folder.id, fileCount: files.length },
    });
    void fileFiles(files, { kind: 'folder', folderId: node.folder.id });
  };

  const startRename = () => {
    setRenameValue(node.folder.name);
    setRenaming(true);
    setMenuOpen(false);
  };

  const submitRename = () => {
    const name = renameValue.trim();
    if (!name || name === node.folder.name) {
      setRenaming(false);
      return;
    }
    renameMut.mutate(
      { folderId: node.folder.id, name },
      { onSettled: () => setRenaming(false) },
    );
  };

  const openInNewTab = () => {
    setMenuOpen(false);
    const folders = queryClient.getQueryData<Folder[]>([
      'folders',
      node.folder.containerId,
    ]);
    const byId = new Map((folders ?? []).map((f) => [f.id, f]));
    const ancestors: string[] = [];
    let cursor: string | null = node.folder.parentId;
    const seen = new Set<string>();
    while (cursor && !seen.has(cursor)) {
      ancestors.push(cursor);
      seen.add(cursor);
      cursor = byId.get(cursor)?.parentId ?? null;
    }
    openTab({
      containerId: node.folder.containerId,
      folderId: node.folder.id,
      expanded: ancestors,
      activate: true,
    });
    addBreadcrumb({
      category: 'ui.click',
      message: 'open-in-new-tab',
      data: { folderId: node.folder.id },
    });
  };

  const startCreateChild = () => {
    setMenuOpen(false);
    setCreatingChild(true);
    setChildName('');
    if (!expanded) toggleExpanded(node.folder.id);
  };

  const submitCreateChild = () => {
    const name = childName.trim();
    if (!name) {
      setCreatingChild(false);
      return;
    }
    createMut.mutate(
      {
        containerId: node.folder.containerId,
        parentId: node.folder.id,
        name,
      },
      {
        onSettled: () => {
          setCreatingChild(false);
          setChildName('');
        },
      },
    );
  };

  return (
    <>
      {/* Drop target spans the whole folder block (name row + its expanded
          contents), so dropping a mail anywhere over the folder — including its
          listed mails or the indented blank area — files into THIS folder. A
          nested folder wins over its parent because its own wrapper stops the
          event from bubbling up. */}
      <div
        data-drop-folder-id={node.folder.id}
        onDragOver={onRowDragOver}
        onDragEnter={onRowDragOver}
        onDragLeave={onRowDragLeave}
        onDrop={onRowDrop}
      >
      <DraggableFolder
        folderId={node.folder.id}
        containerId={node.folder.containerId}
        parentId={node.folder.parentId}
      >
      <div
        className={`${styles.row} ${isActive || selected ? styles.rowActive : ''} ${dropOver ? styles.rowDropOver : ''}`}
        style={{ paddingLeft: `calc(${tokens.spacingHorizontalXS} + ${depth * 12}px)` }}
        aria-selected={selected}
        {...sel.attrs(ref)}
        onClick={onClick}
        onDoubleClick={(e) => {
          if (renaming) return;
          e.stopPropagation();
          // Open the dedicated folder-detail page (breadcrumb + sortable mail
          // table). The chevron still expands inline in the tree.
          openFolderDetail(node.folder.containerId, node.folder.id);
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Cf. MailRow : un clic droit hors sélection l'abandonne, sinon le menu
          // annoncerait un lot auquel cette ligne n'appartient pas.
          if (!selected && sel.items.length > 0) sel.clear();
          setMenuTarget(pointTarget(e.clientX, e.clientY));
          setMenuOpen(true);
        }}
      >
        {isExpandable ? (
          <Button
            appearance="subtle"
            size="small"
            icon={expanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(node.folder.id);
            }}
          />
        ) : (
          <Button
            appearance="subtle"
            size="small"
            icon={<ChevronRight16Regular />}
            className={styles.chevronSpacer}
            disabled
            tabIndex={-1}
            aria-hidden
          />
        )}
        {expanded ? (
          <FolderOpenItemIcon className={styles.rowIcon} />
        ) : (
          <FolderItemIcon className={styles.rowIcon} />
        )}
        {renaming ? (
          <Input
            className={styles.renameInput}
            size="small"
            autoFocus
            value={renameValue}
            disabled={renameMut.isPending}
            onChange={(_e, d) => setRenameValue(d.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={submitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitRename();
              if (e.key === 'Escape') {
                setRenaming(false);
                setRenameValue(node.folder.name);
              }
            }}
          />
        ) : (
          <span className={styles.label} title={node.folder.name}>
            {node.folder.name}
          </span>
        )}
        {fileMail.isPending || isFiling ? (
          <div className={styles.actions}>
            <Tooltip content="Rangement en cours…" relationship="label">
              <Spinner size="tiny" />
            </Tooltip>
          </div>
        ) : (
          <div className={showFileButton ? styles.visible : styles.hidden}>
            <FileHereTooltip items={selection}>
              <Button
                appearance="subtle"
                size="small"
                icon={<MailArrowUp20Regular />}
                aria-label={`Ranger ${selection.length} mail(s) dans ${node.folder.name}`}
                onClick={onFileHere}
              />
            </FileHereTooltip>
          </div>
        )}
        {/* Même wrapper que dans ContainerSection et MailRow, pour la même
            raison : Fluent portalise le MenuPopover mais React fait remonter ses
            événements dans l'arbre REACT — sans cela, un clic sur « Renommer » ou
            « Supprimer » bulle jusqu'au `onClick` de la ligne et déplie le dossier
            au passage. */}
        <div
          className={styles.menuWrap}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
        <Menu
          open={menuOpen}
          onOpenChange={(_e, data) => {
            setMenuOpen(data.open);
            // Oublier l'ancre à la fermeture : sans cela, un clic droit puis un
            // clic sur ⋯ rouvrirait le menu à l'ANCIENNE position du curseur.
            if (!data.open) setMenuTarget(null);
          }}
          positioning={menuPositioning(menuTarget)}
        >
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              size="small"
              icon={<MoreHorizontalRegular />}
              className={hover || menuOpen ? styles.visible : styles.hidden}
              onClick={(e) => {
                e.stopPropagation();
                setMenuTarget(null);
                setMenuOpen(true);
              }}
            />
          </MenuTrigger>
          <MenuPopover>
            {/* Même grammaire que les menus d'espace et de mail : action attendue,
                ouvrir, créer, modifier, puis — isolée — la suppression. Elle
                était auparavant collée à « Déplacer… », donc à un geste de
                distraction près d'un clic destructeur. */}
            <MenuList>
              {multi.isMulti && selected ? (
                multi.items
              ) : (
                <>
              {selection.length > 0 && (
                <>
                  <MenuItem
                    icon={<MailArrowUp20Regular />}
                    onClick={() => {
                      setMenuOpen(false);
                      onFileHere();
                    }}
                  >
                    Ranger {selection.length} mail(s) ici
                  </MenuItem>
                  <MenuDivider />
                </>
              )}
              <MenuItem icon={<TabAddRegular />} onClick={openInNewTab}>
                Ouvrir dans un nouvel onglet
              </MenuItem>
              <MenuItem icon={<FolderAdd20Regular />} onClick={startCreateChild}>
                Nouveau sous-dossier
              </MenuItem>
              {/* Fin de menu PARTAGÉE avec le menu d'un mail et avec la page
                  détail : mêmes actions, mêmes icônes, même ordre. */}
              <ItemActionMenuItems
                onRename={startRename}
                onCopy={() => {
                  setMenuOpen(false);
                  copyFolder(clipboardFolder);
                }}
                onCut={() => {
                  setMenuOpen(false);
                  cutFolder(clipboardFolder);
                }}
                onPaste={() => {
                  setMenuOpen(false);
                  pasteInto(node.folder.id, node.folder.containerId);
                }}
                // Désactivé pendant une copie/déplacement de dossier : ces opérations
                // durent (une copie d'objet S3 par mail) et un second clic lancerait une
                // SECONDE copie.
                pasteDisabled={!canPaste || isBusy}
                pasteHint={
                  pending === 'folder'
                    ? 'Coller le dossier copié dans ce dossier'
                    : pending === 'mails'
                      ? 'Coller les mails copiés dans ce dossier'
                      : undefined
                }
                onCopyTo={() => {
                  setMenuOpen(false);
                  setTargetMode('copy');
                }}
                onMoveTo={() => {
                  setMenuOpen(false);
                  setTargetMode('move');
                }}
                onDelete={() => {
                  setMenuOpen(false);
                  setConfirmDelete(true);
                }}
              />
                </>
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
        </div>
      </div>
      </DraggableFolder>
      {expanded && (
        <div className={styles.children}>
          {node.children.map((child) => (
            <FolderRow key={child.folder.id} node={child} depth={depth + 1} />
          ))}
          <FolderMails folderId={node.folder.id} depth={depth + 1} />
          {creatingChild && (
            <div className={styles.subCreateRow}>
              <Input
                size="small"
                autoFocus
                placeholder="Nom du sous-dossier"
                value={childName}
                disabled={createMut.isPending}
                onChange={(_e, d) => setChildName(d.value)}
                onBlur={submitCreateChild}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitCreateChild();
                  if (e.key === 'Escape') {
                    setCreatingChild(false);
                    setChildName('');
                  }
                }}
              />
            </div>
          )}
        </div>
      )}
      </div>
      <Dialog
        open={confirmDelete}
        onOpenChange={(_e, data) => setConfirmDelete(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Supprimer le dossier ?</DialogTitle>
            <DialogContent>
              « {node.folder.name} » sera supprimé. Les mails et sous-dossiers
              à l'intérieur seront masqués (récupération possible pendant 30
              jours via la corbeille).
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmDelete(false)}>
                Annuler
              </Button>
              <Button
                appearance="primary"
                disabled={deleteMut.isPending}
                onClick={() =>
                  deleteMut.mutate(
                    { folderId: node.folder.id },
                    { onSettled: () => setConfirmDelete(false) },
                  )
                }
              >
                {deleteMut.isPending ? 'Suppression…' : 'Supprimer'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {targetMode && (
        <MoveFolderDialog
          open
          mode={targetMode}
          folder={node.folder}
          onClose={() => setTargetMode(null)}
        />
      )}
      {/* Hors du `MenuList`, cf. MailRow. */}
      {multi.dialogs}
    </>
  );
}
